import React, { useState, useMemo } from 'react';
import lumiObserver from '../lumiObserver';

// ── Punnett square — monohybrid cross Bb × Bb
// The student drags two alleles into each parent slot, sees the 2×2
// offspring grid auto-populate, and can run a random offspring sample
// to compare to the 3:1 theoretical ratio.
// Primary teaching moments:
//   1. Dominant (B) vs recessive (b) → 3:1 phenotype ratio
//   2. Genotype ratio 1:2:1 (BB : Bb : bb)
//   3. Small samples deviate from theoretical ratios (law of large numbers)
// ────────────────────────────────────────────────────────────────────────

const T = {
  bg: '#0D1117', surface: '#161B22', border: '#30363D',
  text: '#E6EDF3', textMid: '#8B949E',
  accent: '#0B8A7E', correct: '#10B981', wrong: '#EF4444', hint: '#F59E0B',
};

const BANK = ['B', 'b'];

export default function PunnettSquare({ interactiveId = 'punnett_bbxbb' }) {
  const [p1, setP1] = useState(['B', 'b']);
  const [p2, setP2] = useState(['B', 'b']);
  const [draggingAllele, setDraggingAllele] = useState(null);
  const [sampleSize, setSampleSize] = useState(10);
  const [sample, setSample] = useState(null); // { B_: n, bb: n }
  const [cellTag, setCellTag] = useState({}); // student labels: { '0,0': 'homozygous', ... }

  // Offspring genotypes
  const grid = useMemo(() => {
    return p1.map(a => p2.map(b => {
      const pair = [a, b].sort((x, y) => (x === 'B' ? -1 : 1)).join('');
      return pair;
    }));
  }, [p1, p2]);

  // Theoretical phenotype ratio — count dominant phenotype vs recessive
  const counts = useMemo(() => {
    let B_ = 0, bb = 0;
    grid.forEach(row => row.forEach(g => {
      if (g.includes('B')) B_++; else bb++;
    }));
    return { B_, bb };
  }, [grid]);

  const emit = (type, payload) => lumiObserver.emit({ interactiveId, type, payload });

  const setParentAllele = (which, idx, allele) => {
    const arr = which === 1 ? [...p1] : [...p2];
    if (arr[idx] === allele) return;
    arr[idx] = allele;
    if (which === 1) setP1(arr); else setP2(arr);
    emit('allele_dragged', { from: `bank_${allele}`, to: `parent${which}_slot${idx + 1}` });
    emit('genotype_changed', { parent: which, slot: idx, allele });
    setSample(null); // reset sample when parents change
  };

  const runSample = () => {
    const flat = grid.flat();            // 4 equally likely outcomes
    let B_ = 0, bb = 0;
    for (let i = 0; i < sampleSize; i++) {
      const pick = flat[Math.floor(Math.random() * flat.length)];
      if (pick.includes('B')) B_++; else bb++;
    }
    const res = { B_, bb, ratio: bb === 0 ? '∞' : (B_ / bb).toFixed(2) };
    setSample({ size: sampleSize, ...res });
    emit('population_run', { size: sampleSize, observed: { B_, bb }, ratio: res.ratio });
  };

  const labelCell = (r, c, tag) => {
    const key = `${r},${c}`;
    const genotype = grid[r][c];
    setCellTag(prev => ({ ...prev, [key]: tag }));
    // Teacher-truth: BB / bb are homozygous, Bb is heterozygous.
    const correctLabel = genotype === 'Bb' ? 'heterozygous' : 'homozygous';
    if (tag !== correctLabel) {
      emit('punnett_wrong', { cell: genotype, student_label: tag, correct_label: correctLabel });
    }
  };

  const AlleleTile = ({ a, sourceLabel }) => (
    <div
      draggable
      onDragStart={() => setDraggingAllele({ allele: a, source: sourceLabel })}
      onDragEnd={() => setDraggingAllele(null)}
      style={{
        width: 40, height: 40, borderRadius: 8,
        background: a === 'B' ? T.accent : '#64748B',
        color: '#fff', fontWeight: 900, fontSize: 18,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'grab', userSelect: 'none',
      }}
      aria-label={`Allele ${a} — drag to a parent slot`}
    >{a}</div>
  );

  const ParentSlot = ({ which, idx }) => (
    <div
      onDragOver={e => e.preventDefault()}
      onDrop={() => { if (draggingAllele) setParentAllele(which, idx, draggingAllele.allele); }}
      style={{
        width: 44, height: 44, borderRadius: 8,
        border: `2px dashed ${T.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 900, fontSize: 18,
        background: (which === 1 ? p1[idx] : p2[idx]) === 'B' ? T.accent + '33' : '#64748B33',
        color: T.text,
      }}
    >{which === 1 ? p1[idx] : p2[idx]}</div>
  );

  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <div style={{ fontSize: 11, color: T.textMid, textTransform: 'uppercase', letterSpacing: 0.7, fontWeight: 700 }}>Biology · HSC Module 5 · Heredity</div>
      <div style={{ fontSize: 18, fontWeight: 800, marginTop: 4 }}>Punnett Square — Bb × Bb</div>
      <div style={{ fontSize: 13, color: T.textMid, marginTop: 4 }}>
        Drag alleles from the bank into each parent's slots. The offspring grid updates live.
      </div>

      {/* Allele bank */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 14 }}>
        <span style={{ fontSize: 11, color: T.textMid, fontWeight: 700 }}>BANK</span>
        {BANK.map(a => <AlleleTile key={a} a={a} sourceLabel="bank" />)}
      </div>

      {/* Parents */}
      <div style={{ display: 'flex', gap: 40, justifyContent: 'center', margin: '16px 0 10px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: T.textMid, marginBottom: 6 }}>Parent 1</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <ParentSlot which={1} idx={0} />
            <ParentSlot which={1} idx={1} />
          </div>
        </div>
        <div style={{ fontSize: 28, color: T.textMid, alignSelf: 'center', marginTop: 16 }}>×</div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: T.textMid, marginBottom: 6 }}>Parent 2</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <ParentSlot which={2} idx={0} />
            <ParentSlot which={2} idx={1} />
          </div>
        </div>
      </div>

      {/* Punnett grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr 1fr', gridTemplateRows: 'auto 1fr 1fr', gap: 2, maxWidth: 260, margin: '10px auto' }}>
        <div />
        <div style={labelCellStyle()}>{p2[0]}</div>
        <div style={labelCellStyle()}>{p2[1]}</div>
        {grid.map((row, r) => (
          <React.Fragment key={r}>
            <div style={labelCellStyle()}>{p1[r]}</div>
            {row.map((g, c) => {
              const tag = cellTag[`${r},${c}`];
              return (
                <div key={c} style={{
                  padding: 10, borderRadius: 6, background: T.bg, border: `1px solid ${T.border}`,
                  textAlign: 'center',
                }}>
                  <div style={{ fontSize: 16, fontWeight: 900 }}>{g}</div>
                  <div style={{ fontSize: 10, color: tag === 'heterozygous' && g === 'Bb' ? T.correct : (tag && ((tag === 'homozygous') === (g !== 'Bb'))) ? T.correct : tag ? T.wrong : T.textMid, marginTop: 2 }}>
                    {tag || (
                      <select onChange={e => labelCell(r, c, e.target.value)}
                        defaultValue=""
                        style={{ background: 'transparent', color: T.textMid, border: 'none', fontSize: 10 }}>
                        <option value="" disabled>label…</option>
                        <option value="homozygous">homozygous</option>
                        <option value="heterozygous">heterozygous</option>
                      </select>
                    )}
                  </div>
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>

      <div style={{ fontSize: 12, color: T.textMid, textAlign: 'center' }}>
        Theoretical phenotype ratio → dominant:recessive = {counts.B_}:{counts.bb}
      </div>

      {/* Sampler */}
      <div style={{ marginTop: 14, padding: 12, background: T.bg, borderRadius: 10, border: `1px solid ${T.border}` }}>
        <div style={{ fontSize: 12, color: T.hint, fontWeight: 800, marginBottom: 6 }}>Run a random offspring sample</div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {[10, 100, 1000].map(n => (
            <button key={n} onClick={() => setSampleSize(n)} style={{
              padding: '6px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
              background: sampleSize === n ? T.accent : T.surface,
              color: sampleSize === n ? '#fff' : T.textMid,
              border: `1px solid ${sampleSize === n ? T.accent : T.border}`,
            }}>{n}</button>
          ))}
          <button onClick={runSample} style={{ padding: '6px 14px', borderRadius: 6, background: T.correct, color: '#fff', border: 'none', fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>Run</button>
        </div>
        {sample && (
          <div style={{ marginTop: 10, fontSize: 13, color: T.text }}>
            Sample of {sample.size}: dominant {sample.B_} · recessive {sample.bb} → ratio {sample.ratio}:1
            {sample.size <= 20 && (
              <div style={{ fontSize: 11, color: T.hint, marginTop: 3 }}>
                Small samples are noisy — the true 3:1 only emerges with hundreds of offspring.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const labelCellStyle = () => ({
  padding: 8, fontWeight: 900, textAlign: 'center',
  background: T.surface, color: T.textMid, borderRadius: 4,
});
